import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Send, Phone, MessageSquare, Plus, Eye, Edit, X, CheckCircle,
  RefreshCw, TrendingUp, FileText, Clock, Calendar, Mail, Building2,
  PhoneCall, Mic, ArrowRight, Search, Filter, MoreVertical, UserPlus,
  Share2, History, AlertCircle, User, Check
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '966568228974';

const LEAD_STATUS = [
  { value: 'new', label: 'جديد', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'تم التواصل', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'interested', label: 'مهتم', color: 'bg-purple-100 text-purple-700' },
  { value: 'proposal_sent', label: 'تم إرسال عرض', color: 'bg-orange-100 text-orange-700' },
  { value: 'converted', label: 'تحول لعميل', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'خسارة', color: 'bg-red-100 text-red-700' },
];

const SOURCES = [
  { value: 'direct', label: 'مباشر' },
  { value: 'referral', label: 'إحالة' },
  { value: 'social_media', label: 'وسائل التواصل' },
  { value: 'website', label: 'الموقع الإلكتروني' },
  { value: 'other', label: 'أخرى' },
];

const SERVICE_TYPES = [
  'استشارة قانونية',
  'قضية جنائية',
  'قضية مدنية',
  'قضية تجارية',
  'قضية عمالية',
  'قضية أحوال شخصية',
  'خدمات الموثق',
  'صياغة عقود',
  'تأسيس شركات',
  'أخرى',
];

export default function MarketerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contactLogs, setContactLogs] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  
  // حالات المودالات
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [referNotes, setReferNotes] = useState('');
  
  // بيانات النماذج
  const [leadForm, setLeadForm] = useState({
    full_name: '', phone: '', email: '', company: '', source: 'direct', interest: '', notes: ''
  });
  const [proposalForm, setProposalForm] = useState({
    lead_id: '', lead_name: '', phone: '', email: '', title: '', service_type: '', description: '', amount: 0, discount: 0, validity_days: 30
  });
  const [contactForm, setContactForm] = useState({
    contact_type: 'whatsapp_text', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setUser(userData);
      if (userData.role !== 'marketer' && userData.role !== 'admin') {
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, leadsRes, proposalsRes, logsRes, followUpsRes, referralsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/marketer/dashboard`, { headers }),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/proposals`, { headers }),
        axios.get(`${API}/contact-logs`, { headers }),
        axios.get(`${API}/lead-follow-ups`, { headers }).catch(() => ({ data: { follow_ups: [] } })),
        axios.get(`${API}/lead-referrals`, { headers }).catch(() => ({ data: { referrals: [] } })),
        axios.get(`${API}/employees/list`, { headers }).catch(() => ({ data: { employees: [] } }))
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data || []);
      setProposals(proposalsRes.data || []);
      setContactLogs(logsRes.data || []);
      setFollowUps(followUpsRes.data.follow_ups || []);
      setReferrals(referralsRes.data.referrals || []);
      setEmployees(employeesRes.data.employees || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const createLead = async () => {
    if (!leadForm.full_name || !leadForm.phone) {
      alert('يرجى إدخال الاسم ورقم الجوال');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leads`, leadForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إضافة العميل المحتمل بنجاح');
      setShowLeadModal(false);
      setLeadForm({ full_name: '', phone: '', email: '', company: '', source: 'direct', interest: '', notes: '' });
      fetchData();
    } catch (err) {
      alert('حدث خطأ');
    }
    setSaving(false);
  };

  const createProposal = async () => {
    if (!proposalForm.phone || !proposalForm.title) {
      alert('يرجى إدخال البيانات المطلوبة');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/proposals`, proposalForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم إنشاء العرض بنجاح');
      setShowProposalModal(false);
      fetchData();
    } catch (err) {
      alert('حدث خطأ');
    }
    setSaving(false);
  };

  const logContact = async (lead) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contact-logs`, null, {
        params: {
          contact_type: contactForm.contact_type,
          phone: lead.phone,
          lead_id: lead.id,
          lead_name: lead.full_name,
          notes: contactForm.notes
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم تسجيل التواصل');
      setShowContactModal(false);
      setContactForm({ contact_type: 'whatsapp_text', notes: '' });
      fetchData();
    } catch (err) {
      alert('حدث خطأ');
    }
    setSaving(false);
  };

  // إحالة عميل أو عدة عملاء
  const referLeads = async () => {
    if (selectedEmployees.length === 0) {
      alert('يرجى اختيار موظف واحد على الأقل');
      return;
    }
    if (selectedLeads.length === 0 && !selectedLead) {
      alert('يرجى اختيار عميل واحد على الأقل');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const leadIds = selectedLead ? [selectedLead.id] : selectedLeads;
      
      if (leadIds.length === 1) {
        await axios.post(`${API}/leads/${leadIds[0]}/refer`, null, {
          params: {
            employee_ids: selectedEmployees,
            notes: referNotes
          },
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/leads/refer-multiple`, null, {
          params: {
            lead_ids: leadIds,
            employee_ids: selectedEmployees,
            notes: referNotes
          },
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      alert(`تم إحالة ${leadIds.length} عميل بنجاح`);
      setShowReferModal(false);
      setSelectedLeads([]);
      setSelectedEmployees([]);
      setReferNotes('');
      setSelectedLead(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'حدث خطأ');
    }
    setSaving(false);
  };

  // تبديل اختيار عميل
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // تبديل اختيار موظف
  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployees(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const openWhatsApp = (phone, message = '') => {
    let cleanNumber = phone.replace(/[\s\-\(\)]/g, '');
    if (cleanNumber.startsWith('05')) cleanNumber = '966' + cleanNumber.substring(1);
    else if (cleanNumber.startsWith('5')) cleanNumber = '966' + cleanNumber;
    else if (!cleanNumber.startsWith('966') && !cleanNumber.startsWith('+')) cleanNumber = '966' + cleanNumber;
    cleanNumber = cleanNumber.replace('+', '');
    
    const url = message 
      ? `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/${cleanNumber}`;
    window.open(url, '_blank');
  };

  const sendProposalViaWhatsApp = async (proposal) => {
    const message = `مرحباً ${proposal.lead_name || proposal.client_name}،

مكتب المحامي هشام يوسف الخياط يُحييك.

📋 *عرض خدمات قانونية*
━━━━━━━━━━━━━━━━
📌 رقم العرض: ${proposal.proposal_number}
📝 العنوان: ${proposal.title}
🏷️ نوع الخدمة: ${proposal.service_type}

💰 المبلغ: ${proposal.amount} ريال
${proposal.discount > 0 ? `🎁 الخصم: ${proposal.discount} ريال` : ''}
✅ المبلغ النهائي: ${proposal.final_amount} ريال

📅 صلاحية العرض: ${proposal.validity_days} يوم

للموافقة أو الاستفسار، نحن في خدمتك.

مكتب المحامي هشام يوسف الخياط
📞 ${WHATSAPP_NUMBER}`;

    openWhatsApp(proposal.phone, message);
    
    // تحديث حالة العرض
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/proposals/${proposal.id}/send?send_via=whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error updating proposal:', err);
    }
  };

  const getStatusBadge = (status) => {
    const opt = LEAD_STATUS.find(s => s.value === status);
    return <span className={`px-2 py-1 rounded-full text-xs ${opt?.color || 'bg-gray-100 text-gray-600'}`}>{opt?.label || status}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ar-SA');
  };

  const filteredLeads = leads.filter(lead =>
    lead.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lead.phone?.includes(searchQuery) ||
    lead.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      {/* Header */}
      <header className="bg-gradient-to-l from-green-600 to-green-700 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <h1 className="text-xl font-heading font-bold text-white">لوحة التسويق</h1>
              <p className="text-xs text-white/70">{user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="p-2 bg-white/20 text-white rounded-lg hover:bg-white/30">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 text-sm">
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* التبويبات */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'الرئيسية', icon: TrendingUp },
            { id: 'leads', label: 'العملاء المحتملين', icon: Users },
            { id: 'follow-ups', label: 'آخر التحديثات', icon: History },
            { id: 'referrals', label: 'الإحالات', icon: Share2 },
            { id: 'proposals', label: 'العروض', icon: FileText },
            { id: 'contacts', label: 'سجل التواصل', icon: Phone },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'referrals' && referrals.filter(r => r.status === 'pending').length > 0 && (
                <span className="w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {referrals.filter(r => r.status === 'pending').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* لوحة الرئيسية */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* بطاقات الإحصائيات */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-blue-500">
                <Users className="w-8 h-8 text-blue-500 mb-2" />
                <p className="text-3xl font-bold text-primary">{stats.leads.total}</p>
                <p className="text-sm text-gray-500">إجمالي العملاء المحتملين</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-yellow-500">
                <Clock className="w-8 h-8 text-yellow-500 mb-2" />
                <p className="text-3xl font-bold text-yellow-600">{stats.leads.new}</p>
                <p className="text-sm text-gray-500">جدد</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-green-500">
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <p className="text-3xl font-bold text-green-600">{stats.leads.converted}</p>
                <p className="text-sm text-gray-500">تحولوا لعملاء</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-purple-500">
                <Phone className="w-8 h-8 text-purple-500 mb-2" />
                <p className="text-3xl font-bold text-purple-600">{stats.today_contacts}</p>
                <p className="text-sm text-gray-500">تواصلات اليوم</p>
              </div>
            </div>

            {/* إحصائيات العروض */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-heading font-semibold text-primary mb-4">إحصائيات العروض</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-primary">{stats.proposals.total}</p>
                  <p className="text-sm text-gray-500">إجمالي العروض</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-orange-600">{stats.proposals.sent}</p>
                  <p className="text-sm text-gray-500">مرسلة</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-green-600">{stats.proposals.accepted}</p>
                  <p className="text-sm text-gray-500">مقبولة</p>
                </div>
              </div>
            </div>

            {/* أزرار الإجراءات السريعة */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                onClick={() => setShowLeadModal(true)}
                className="p-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex flex-col items-center gap-2"
              >
                <Plus className="w-6 h-6" />
                <span>إضافة عميل محتمل</span>
              </button>
              <button
                onClick={() => setShowProposalModal(true)}
                className="p-4 bg-green-600 text-white rounded-xl hover:bg-green-700 flex flex-col items-center gap-2"
              >
                <FileText className="w-6 h-6" />
                <span>إنشاء عرض</span>
              </button>
              <button
                onClick={() => openWhatsApp(WHATSAPP_NUMBER)}
                className="p-4 bg-[#25D366] text-white rounded-xl hover:opacity-90 flex flex-col items-center gap-2"
              >
                <MessageSquare className="w-6 h-6" />
                <span>واتساب الرسائل</span>
              </button>
              <button
                onClick={() => window.open(`tel:${WHATSAPP_NUMBER}`, '_self')}
                className="p-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 flex flex-col items-center gap-2"
              >
                <PhoneCall className="w-6 h-6" />
                <span>مكالمة صوتية</span>
              </button>
            </div>
          </div>
        )}

        {/* قائمة العملاء المحتملين */}
        {activeTab === 'leads' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="بحث..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <button
                onClick={() => setShowLeadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                إضافة
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* شريط أدوات إضافي */}
              {selectedLeads.length > 0 && (
                <div className="bg-green-50 px-4 py-3 border-b flex items-center justify-between">
                  <span className="text-green-700 font-medium">
                    تم اختيار {selectedLeads.length} عميل
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedLead(null);
                        setShowReferModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Share2 className="w-4 h-4" />
                      إحالة للموظفين
                    </button>
                    <button
                      onClick={() => setSelectedLeads([])}
                      className="px-4 py-2 text-gray-600 bg-white rounded-lg hover:bg-gray-100"
                    >
                      إلغاء التحديد
                    </button>
                  </div>
                </div>
              )}
              
              <table className="w-full">
                <thead className="bg-green-600 text-white">
                  <tr>
                    <th className="px-3 py-3 text-center w-12">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeads(filteredLeads.map(l => l.id));
                          } else {
                            setSelectedLeads([]);
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-right text-sm">الرقم</th>
                    <th className="px-4 py-3 text-right text-sm">الاسم</th>
                    <th className="px-4 py-3 text-right text-sm">الجوال</th>
                    <th className="px-4 py-3 text-right text-sm">الشركة</th>
                    <th className="px-4 py-3 text-center text-sm">الحالة</th>
                    <th className="px-4 py-3 text-center text-sm">محال إلى</th>
                    <th className="px-4 py-3 text-center text-sm">إجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredLeads.map((lead, idx) => (
                    <tr key={idx} className={`hover:bg-gray-50 ${selectedLeads.includes(lead.id) ? 'bg-green-50' : ''}`}>
                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-green-600">{lead.lead_number}</td>
                      <td className="px-4 py-3 text-sm font-semibold">{lead.full_name}</td>
                      <td className="px-4 py-3 text-sm" dir="ltr">{lead.phone}</td>
                      <td className="px-4 py-3 text-sm">{lead.company || '-'}</td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(lead.status)}</td>
                      <td className="px-4 py-3 text-center text-sm">
                        {lead.is_referred ? (
                          <span className="text-blue-600 text-xs">
                            {lead.referred_to_names?.join(', ') || 'تم الإحالة'}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => {
                              openWhatsApp(lead.phone);
                              setSelectedLead(lead);
                              setShowContactModal(true);
                            }}
                            className="p-1.5 text-[#25D366] hover:bg-green-50 rounded"
                            title="واتساب"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowReferModal(true);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="إحالة لموظف"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setProposalForm({
                                ...proposalForm,
                                lead_id: lead.id,
                                lead_name: lead.full_name,
                                phone: lead.phone,
                                email: lead.email || ''
                              });
                              setShowProposalModal(true);
                            }}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                            title="إنشاء عرض"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setSelectedLead(lead)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="التفاصيل"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredLeads.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">لا يوجد عملاء محتملين</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* تبويب آخر التحديثات */}
        {activeTab === 'follow-ups' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h3 className="font-semibold text-primary flex items-center gap-2">
                <History className="w-5 h-5" />
                آخر تحديثات العملاء المحتملين
              </h3>
              <span className="text-sm text-gray-500">{followUps.length} تحديث</span>
            </div>
            
            <div className="divide-y">
              {followUps.map((log, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      log.action === 'referral' ? 'bg-blue-100' :
                      log.action === 'status_update' ? 'bg-green-100' :
                      log.action === 'referral_response' ? 'bg-purple-100' :
                      'bg-gray-100'
                    }`}>
                      {log.action === 'referral' ? <Share2 className="w-5 h-5 text-blue-600" /> :
                       log.action === 'status_update' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                       log.action === 'referral_response' ? <MessageSquare className="w-5 h-5 text-purple-600" /> :
                       <Edit className="w-5 h-5 text-gray-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-primary">{log.lead_name}</h4>
                        <span className="text-xs text-gray-400">{formatDate(log.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{log.details}</p>
                      {log.notes && (
                        <p className="text-xs text-gray-500 mt-1 bg-gray-50 px-2 py-1 rounded">
                          ملاحظات: {log.notes}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">بواسطة: {log.done_by_name}</p>
                    </div>
                  </div>
                </div>
              ))}
              {followUps.length === 0 && (
                <div className="p-12 text-center">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">لا توجد تحديثات بعد</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* تبويب الإحالات */}
        {activeTab === 'referrals' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-primary flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  إحالات العملاء للموظفين
                </h3>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                    قيد الانتظار: {referrals.filter(r => r.status === 'pending').length}
                  </span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    جاري المتابعة: {referrals.filter(r => r.status === 'in_progress').length}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                    مكتملة: {referrals.filter(r => r.status === 'completed').length}
                  </span>
                </div>
              </div>
              
              <table className="w-full">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm">العميل</th>
                    <th className="px-4 py-3 text-right text-sm">الجوال</th>
                    <th className="px-4 py-3 text-right text-sm">محال إلى</th>
                    <th className="px-4 py-3 text-right text-sm">من قِبل</th>
                    <th className="px-4 py-3 text-center text-sm">الحالة</th>
                    <th className="px-4 py-3 text-right text-sm">التاريخ</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {referrals.map((ref, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold">{ref.lead_name}</td>
                      <td className="px-4 py-3 text-sm" dir="ltr">{ref.lead_phone}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {ref.referred_to_names?.map((name, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                              {name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ref.referred_by_name}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          ref.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          ref.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {ref.status === 'pending' ? 'قيد الانتظار' :
                           ref.status === 'in_progress' ? 'جاري المتابعة' :
                           ref.status === 'completed' ? 'مكتمل' : ref.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(ref.created_at)}</td>
                    </tr>
                  ))}
                  {referrals.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                        <Share2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p>لا توجد إحالات بعد</p>
                        <p className="text-sm mt-2">يمكنك إحالة عملاء من تبويب "العملاء المحتملين"</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* قائمة العروض */}
        {activeTab === 'proposals' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setShowProposalModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                إنشاء عرض
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {proposals.map((proposal, idx) => (
                <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border-r-4 border-green-500">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-mono text-sm text-green-600">{proposal.proposal_number}</span>
                      <h4 className="font-heading font-semibold text-primary">{proposal.title}</h4>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      proposal.status === 'sent' ? 'bg-orange-100 text-orange-700' :
                      proposal.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {proposal.status === 'draft' ? 'مسودة' : proposal.status === 'sent' ? 'مرسل' : proposal.status === 'accepted' ? 'مقبول' : proposal.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{proposal.lead_name || proposal.client_name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-green-600">{proposal.final_amount} ريال</span>
                    {proposal.status === 'draft' && (
                      <button
                        onClick={() => sendProposalViaWhatsApp(proposal)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#25D366] text-white rounded-lg text-sm hover:opacity-90"
                      >
                        <Send className="w-4 h-4" />
                        إرسال عبر واتساب
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {proposals.length === 0 && (
                <div className="col-span-2 bg-white rounded-xl p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">لا توجد عروض</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* سجل التواصل */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-purple-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-right text-sm">التاريخ</th>
                  <th className="px-4 py-3 text-right text-sm">النوع</th>
                  <th className="px-4 py-3 text-right text-sm">العميل</th>
                  <th className="px-4 py-3 text-right text-sm">الجوال</th>
                  <th className="px-4 py-3 text-right text-sm">ملاحظات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contactLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        log.contact_type === 'whatsapp_text' ? 'bg-green-100 text-green-700' :
                        log.contact_type === 'whatsapp_voice' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {log.contact_type === 'whatsapp_text' ? <MessageSquare className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                        {log.contact_type === 'whatsapp_text' ? 'واتساب' : log.contact_type === 'whatsapp_voice' ? 'صوتي' : 'مكالمة'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{log.lead_name || log.client_name || '-'}</td>
                    <td className="px-4 py-3 text-sm" dir="ltr">{log.phone}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{log.notes || '-'}</td>
                  </tr>
                ))}
                {contactLogs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">لا يوجد سجلات</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* مودال إضافة عميل محتمل */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between bg-green-600 text-white rounded-t-xl">
              <h3 className="font-heading font-semibold">إضافة عميل محتمل</h3>
              <button onClick={() => setShowLeadModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <input type="text" placeholder="الاسم الكامل *" value={leadForm.full_name} onChange={(e) => setLeadForm({...leadForm, full_name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input type="tel" placeholder="رقم الجوال *" value={leadForm.phone} onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" dir="ltr" />
              <input type="email" placeholder="البريد الإلكتروني" value={leadForm.email} onChange={(e) => setLeadForm({...leadForm, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input type="text" placeholder="الشركة" value={leadForm.company} onChange={(e) => setLeadForm({...leadForm, company: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <select value={leadForm.source} onChange={(e) => setLeadForm({...leadForm, source: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={leadForm.interest} onChange={(e) => setLeadForm({...leadForm, interest: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="">الخدمة المهتم بها</option>
                {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea placeholder="ملاحظات" value={leadForm.notes} onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})} rows={3} className="w-full px-4 py-2 border rounded-lg" />
              <button onClick={createLead} disabled={saving} className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {saving ? 'جاري الحفظ...' : 'حفظ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال إنشاء عرض */}
      {showProposalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between bg-blue-600 text-white rounded-t-xl">
              <h3 className="font-heading font-semibold">إنشاء عرض</h3>
              <button onClick={() => setShowProposalModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {proposalForm.lead_name && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <span className="text-gray-500">العميل المحتمل:</span> <strong>{proposalForm.lead_name}</strong>
                </div>
              )}
              <input type="text" placeholder="عنوان العرض *" value={proposalForm.title} onChange={(e) => setProposalForm({...proposalForm, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              <input type="tel" placeholder="رقم الجوال *" value={proposalForm.phone} onChange={(e) => setProposalForm({...proposalForm, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" dir="ltr" />
              <select value={proposalForm.service_type} onChange={(e) => setProposalForm({...proposalForm, service_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="">نوع الخدمة</option>
                {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea placeholder="تفاصيل العرض" value={proposalForm.description} onChange={(e) => setProposalForm({...proposalForm, description: e.target.value})} rows={3} className="w-full px-4 py-2 border rounded-lg" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">المبلغ (ريال)</label>
                  <input type="number" value={proposalForm.amount} onChange={(e) => setProposalForm({...proposalForm, amount: parseFloat(e.target.value) || 0})} className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">الخصم (ريال)</label>
                  <input type="number" value={proposalForm.discount} onChange={(e) => setProposalForm({...proposalForm, discount: parseFloat(e.target.value) || 0})} className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <span className="text-sm text-gray-600">المبلغ النهائي:</span>
                <span className="text-xl font-bold text-green-600 mr-2">{proposalForm.amount - proposalForm.discount} ريال</span>
              </div>
              <button onClick={createProposal} disabled={saving} className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'جاري الحفظ...' : 'إنشاء العرض'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال تسجيل التواصل */}
      {showContactModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between bg-purple-600 text-white rounded-t-xl">
              <h3 className="font-heading font-semibold">تسجيل تواصل</h3>
              <button onClick={() => setShowContactModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">تسجيل تواصل مع: <strong>{selectedLead.full_name}</strong></p>
              <select value={contactForm.contact_type} onChange={(e) => setContactForm({...contactForm, contact_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="whatsapp_text">واتساب - رسالة</option>
                <option value="whatsapp_voice">واتساب - صوتي</option>
                <option value="call">مكالمة هاتفية</option>
              </select>
              <textarea placeholder="ملاحظات التواصل" value={contactForm.notes} onChange={(e) => setContactForm({...contactForm, notes: e.target.value})} rows={3} className="w-full px-4 py-2 border rounded-lg" />
              <button onClick={() => logContact(selectedLead)} disabled={saving} className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {saving ? 'جاري الحفظ...' : 'تسجيل التواصل'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال إحالة العملاء */}
      {showReferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-blue-600 text-white rounded-t-xl">
              <h3 className="font-heading font-semibold flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                إحالة عملاء للموظفين
              </h3>
              <button onClick={() => {
                setShowReferModal(false);
                setSelectedLead(null);
                setSelectedEmployees([]);
                setReferNotes('');
              }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* العميل المحدد */}
              {selectedLead && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-600 mb-1">العميل المحدد:</p>
                  <p className="font-semibold">{selectedLead.full_name}</p>
                  <p className="text-sm text-gray-600" dir="ltr">{selectedLead.phone}</p>
                </div>
              )}
              
              {/* عدة عملاء */}
              {!selectedLead && selectedLeads.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-blue-600 mb-2">العملاء المحددين ({selectedLeads.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLeads.map(leadId => {
                      const lead = leads.find(l => l.id === leadId);
                      return lead && (
                        <span key={leadId} className="px-2 py-1 bg-white rounded text-sm">
                          {lead.full_name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* اختيار الموظفين */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">اختر الموظفين للإحالة:</p>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {employees.filter(emp => emp.role !== 'client' && emp.role !== 'marketer').map(emp => (
                    <label
                      key={emp.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                        selectedEmployees.includes(emp.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.id)}
                        onChange={() => toggleEmployeeSelection(emp.id)}
                        className="w-5 h-5 rounded text-blue-600"
                      />
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{emp.full_name}</p>
                        <p className="text-xs text-gray-500">
                          {emp.role === 'admin' ? 'مدير' :
                           emp.role === 'lawyer' ? 'محامي' :
                           emp.role === 'staff' ? 'موظف' :
                           emp.role === 'accountant' ? 'محاسب' : emp.role}
                        </p>
                      </div>
                      {selectedEmployees.includes(emp.id) && (
                        <Check className="w-5 h-5 text-blue-600 mr-auto" />
                      )}
                    </label>
                  ))}
                  {employees.filter(emp => emp.role !== 'client' && emp.role !== 'marketer').length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">لا يوجد موظفين</p>
                    </div>
                  )}
                </div>
              </div>

              {/* ملاحظات الإحالة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات الإحالة:</label>
                <textarea
                  value={referNotes}
                  onChange={(e) => setReferNotes(e.target.value)}
                  placeholder="أضف ملاحظات أو تعليمات للموظفين..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* ملخص الإحالة */}
              {selectedEmployees.length > 0 && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm text-green-700">
                    سيتم إحالة <strong>{selectedLead ? 1 : selectedLeads.length}</strong> عميل إلى <strong>{selectedEmployees.length}</strong> موظف
                  </p>
                  <p className="text-xs text-green-600 mt-1">سيتم إرسال إشعار فوري للموظفين المحددين</p>
                </div>
              )}

              {/* أزرار */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowReferModal(false);
                    setSelectedLead(null);
                    setSelectedEmployees([]);
                    setReferNotes('');
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={referLeads}
                  disabled={saving || selectedEmployees.length === 0}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>جاري الإحالة...</>
                  ) : (
                    <>
                      <Share2 className="w-5 h-5" />
                      إحالة الآن
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
